import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, SubHeader, Table } from 'vetrf-ui';
import { ProductType } from 'components/registry/types/api';
import { RemoveModal } from 'components/RemoveModal';
import { entryRegistrationApi } from 'api/entryRegistration';
import { EntryRegistrationDTO } from 'api/entryRegistration/types';
import { EditRegistrationModal } from './components/EditRegistrationModal';
import { getColumns } from './consts';

type EntryRegistrationProps = {
  id: string;
  access: boolean;
  productType: ProductType;
};

export const EntryRegistration = ({ id, access, productType }: EntryRegistrationProps) => {
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [editReg, setEditReg] = useState<EntryRegistrationDTO>();
  const [removeReg, setRemoveReg] = useState<EntryRegistrationDTO>();
  const [tableData, setTableData] = useState<EntryRegistrationDTO[]>([]);
  const [error, setError] = useState('');

  const toggleModal = (reg?: EntryRegistrationDTO) => {
    setEditReg(reg);
    setIsRegistrationModalOpen((prev) => !prev);
  };

  const fetchTable = async () => {
    setError('');
    await entryRegistrationApi
      .get(id)
      .then(setTableData)
      .catch((e) => setError(e.response.data.message));
  };

  const onAdd = (values: EntryRegistrationDTO) => {
    setError('');
    entryRegistrationApi
      .add(values, id)
      .then(fetchTable)
      .catch((e) => setError(e.response.data.message))
      .finally(() => toggleModal());
  };

  const onEdit = async (data: EntryRegistrationDTO) => {
    setError('');
    await entryRegistrationApi
      .edit(data, id)
      .then(fetchTable)
      .catch((e) => setError(e.response.data.message))
      .finally(() => toggleModal());
  };

  const onRemove = async (regId: string) => {
    setError('');
    await entryRegistrationApi
      .remove(id, regId)
      .then(fetchTable)
      .catch((e) => setError(e.response.data.message))
      .finally(() => setRemoveReg(undefined));
  };

  const filteredColumns = useMemo(() => {
    const cols = getColumns().filter(
      (col) => !col.productTypes || col.productTypes.includes(productType)
    );

    if (access) {
      cols.push({
        dataFormatter: (item) => (
          <>
            <Button
              icon="pencil"
              iconColor="green"
              iconSize={150}
              onClick={() => toggleModal(item as EntryRegistrationDTO)}
              onlyIcon
              tooltip="Редактировать"
            />
            <Button
              icon="trash"
              iconColor="red"
              iconSize={150}
              onClick={() => setRemoveReg(item as EntryRegistrationDTO)}
              onlyIcon
              tooltip="Удалить"
            />
          </>
        ),
        className: 'actions',
        key: 'actions',
        title: '',
        width: '1%',
        enableFilter: false,
        enableSort: false,
      });
    }
    return cols;
  }, [productType, access]);

  useEffect(() => {
    fetchTable();
  }, []);

  return (
    <>
      <SubHeader
        header="Государственная регистрация"
        icon="book"
        iconColor="orange"
        iconSize={100}
        level={4}
        toolbar={
          access && (
            <Button
              icon="plus"
              color="info"
              size="minier"
              onClick={() => toggleModal()}
              text="Добавить"
            />
          )
        }
      />
      <div>
        {error && (
          <Alert closable messageType="danger">
            {error}
          </Alert>
        )}

        <div className="table-container">
          <Table columns={filteredColumns} dataList={tableData} />
        </div>

        {isRegistrationModalOpen && (
          <EditRegistrationModal
            defaultValues={editReg}
            productType={productType}
            handleSubmit={editReg ? onEdit : onAdd}
            onClose={() => toggleModal()}
          />
        )}

        {!!removeReg && (
          <RemoveModal
            onClose={() => setRemoveReg(undefined)}
            onRemove={() => onRemove(removeReg.id)}
            modalContent="Вы уверены, что хотите удалить информацию о регистрации?"
          />
        )}
      </div>
    </>
  );
};
